import { create } from "zustand";

export type PriceMonitorSource = "item" | "favorites" | "product";

export type PriceMonitorRequestOptions = {
  favoriteId?: number | null;
  source?: PriceMonitorSource;
};

export type PriceMonitorRequest = {
  item: SearchResultItem;
  options?: PriceMonitorRequestOptions;
};

export type PriceMonitorRequestContext = {
  pathname: string;
  sourceModalOpen: boolean;
};

export type PendingPriceMonitorRequest = PriceMonitorRequest &
  PriceMonitorRequestContext & {
    requestToken: string;
    requestedAt: number;
    expiresAt: number;
  };

export const PRICE_MONITOR_REQUEST_TTL_MS = 3 * 60 * 1000;

let requestSequence = 0;

export const isPendingPriceMonitorRequestExpired = (
  request: PendingPriceMonitorRequest,
  now = Date.now()
) => request.expiresAt <= now;

type PriceMonitorRequestState = {
  pendingRequest: PendingPriceMonitorRequest | null;
  signInStarted: boolean;
  actions: {
    queue: (
      request: PriceMonitorRequest,
      context: PriceMonitorRequestContext
    ) => PendingPriceMonitorRequest;
    markSignInStarted: (requestToken: string) => void;
    takePending: (requestToken: string) => PendingPriceMonitorRequest | null;
    clear: (requestToken?: string) => void;
  };
};

export const usePriceMonitorRequestStore = create<PriceMonitorRequestState>((set, get) => ({
  pendingRequest: null,
  signInStarted: false,
  actions: {
    queue: (request, context) => {
      const requestedAt = Date.now();
      const pendingRequest = {
        ...request,
        ...context,
        requestToken: `${requestedAt}-${++requestSequence}`,
        requestedAt,
        expiresAt: requestedAt + PRICE_MONITOR_REQUEST_TTL_MS,
      };
      set({ pendingRequest, signInStarted: false });
      return pendingRequest;
    },
    markSignInStarted: (requestToken) =>
      set((state) =>
        state.pendingRequest?.requestToken === requestToken ? { signInStarted: true } : state
      ),
    takePending: (requestToken) => {
      const pendingRequest = get().pendingRequest;
      if (!pendingRequest || pendingRequest.requestToken !== requestToken) return null;

      set({ pendingRequest: null, signInStarted: false });
      if (isPendingPriceMonitorRequestExpired(pendingRequest)) return null;

      return pendingRequest;
    },
    clear: (requestToken) =>
      set((state) => {
        if (requestToken && state.pendingRequest?.requestToken !== requestToken) return state;
        return { pendingRequest: null, signInStarted: false };
      }),
  },
}));
